import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/vosart";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const vosart: CaseStudy = {
  slug: "vosart",
  title: "VOSART",
  cardTitle: "Разработка интернет-магазина дизайнерской мебели VOSART",
  subtitle: "Мебельная фабрика",
  industry: { label: "Дом, мебель, свет", slug: "furniture" },
  eyebrow: "Интернет-магазин · Мебельная фабрика",
  h1: "Разработка интернет-магазина дизайнерской мебели VOSART",
  lead:
    "Разработали интернет-магазин мебельной фабрики VOSART: каталог диванов и кроватей с фильтрацией, избранное, сравнение товаров, раздел проектов и B2B-страницы для партнёров.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить интернет-магазин мебели" },
  siteUnavailable: true,

  seo: {
    primaryKeyword: "разработка интернет-магазина дизайнерской мебели",
    secondaryKeywords: [
      "сайт мебельной фабрики разработка",
      "интернет-магазин мебели разработка",
      "e-commerce для мебельного бренда",
      "сайт производителя мягкой мебели",
    ],
    semanticEntities: [
      "каталог диванов",
      "каталог кроватей",
      "фильтрация по параметрам",
      "избранное",
      "сравнение товаров",
      "раздел проектов",
      "B2B-раздел",
    ],
    excludedKeywords: [
      "купить диван",
      "диван-кровать цена",
      "дизайнерская мебель купить",
      "кровать Москва",
    ],
    title: "Интернет-магазин VOSART — кейс HAY.agency",
    description:
      "Кейс HAY.agency: сайт мебельной фабрики VOSART с каталогом диванов и кроватей, фильтрацией, избранным, сравнением, проектами и B2B-разделами.",
    h1: "Разработка интернет-магазина дизайнерской мебели VOSART",
    slug: "vosart",
    canonicalUrl: `${SITE_URL}/cases/vosart`,
    ogTitle: "Интернет-магазин мебельной фабрики VOSART",
    ogDescription:
      "Каталог диванов и кроватей, фильтрация, избранное, сравнение и B2B-раздел для мебельной фабрики.",
    ogImage: `${SITE_URL}${dir}/vosart-og.webp`,
    robotsIndex: true,
    datePublished: "2022-01-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка и развитие интернет-магазинов",
      to: routes.ecom,
      note: "Каталог, карточка товара и фильтрация",
    },
    relatedIndustry: {
      label: "SEO-продвижение интернет-магазинов",
      to: routes.seo,
      note: "Видимость каталога и категорий",
    },
    relatedCases: [
      {
        label: "E-commerce платформа для производителя косметики Levrana",
        to: "/cases/levrana",
        note: "Мультибрендовый каталог с фильтрацией",
      },
      {
        label: "Разработка интернет-магазина цветов «Цветочный маркет»",
        to: "/cases/cvmarket",
        note: "Каталог, карточка товара и корзина",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен интернет-магазин мебельного бренда?",
    text: "Спроектируем каталог с фильтрацией, карточку товара, сравнение и B2B-раздел.",
    button: "Обсудить интернет-магазин мебели",
  },

  client: {
    name: "VOSART",
    url: "https://vosart.com/",
    city: "Москва",
    industry: "Мебельная фабрика",
  },
  year: "С 2022 года",
  services: [
    "Дизайн интернет-магазина",
    "Разработка каталога диванов и кроватей",
    "Фильтрация, избранное и сравнение товаров",
    "Раздел проектов и B2B-страницы",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "Разработка", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "VOSART" },
    { label: "Отрасль", value: "Мебельная фабрика" },
    { label: "Тип проекта", value: "Интернет-магазин дизайнерской мебели" },
    { label: "Платформа", value: "1С-Битрикс" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Каталог диванов и кроватей с фильтрацией по параметрам",
      "Карточка товара с ключевыми характеристиками",
      "Избранное и сравнение товаров",
      "Раздел готовых проектов с мебелью в интерьере",
      "B2B-раздел для партнёров и оптовых клиентов",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "VOSART — мебельная фабрика, выпускающая диваны и кровати. Нужен был интернет-магазин, который выдерживает каталог с большим числом моделей и параметров, а покупателю позволяет быстро сравнить варианты и выбрать подходящий.",
        "Отдельная часть задачи — B2B-раздел для партнёров фабрики, работающий отдельно от розничного каталога.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: тёплая подача мебели в интерьере",
    paragraphs: [
      "Интерфейс собран на тёплой stone/taupe подложке, которая перекликается с материалами мебели и не спорит с фотографиями товара.",
      "Карточка товара и раздел проектов показывают мебель в интерьере: покупатель видит масштаб предмета и сочетание материалов в реальном пространстве.",
    ],
    bullets: [
      "Тёплая нейтральная подложка вместо белого фона",
      "Фильтрация каталога по ключевым параметрам",
      "Сравнение и избранное как отдельные пользовательские сценарии",
    ],
  },

  identity: {
    fonts: [],
    colors: [
      { hex: "#EFE8DE", name: "Фон витрины" },
      { hex: "#8A7458", name: "Акцент бренда" },
      { hex: "#211E1A", name: "Текст" },
    ],
  },

  cover: img("vosart-case-cover", "Главная страница интернет-магазина мебели VOSART", 6),

  gallery: [
    img("vosart-catalog", "Каталог диванов и кроватей VOSART с фильтрацией", 6),
    img("vosart-product", "Карточка товара интернет-магазина VOSART", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия повторяет логику десктопа: каталог с фильтрацией, карточка товара, избранное и сравнение.",
      "Состояния каталога и избранного в мобильной версии показаны заполненными.",
    ],
    images: [
      mobile("vosart-mobile-hero", "Мобильная главная интернет-магазина VOSART"),
      mobile("vosart-mobile-catalog", "Мобильный каталог мебели VOSART"),
    ],
  },

  columnBlocks: [
    {
      id: "catalog",
      title: "Продуктовая система",
      paragraphs: [
        "Каталог организован по коллекциям диванов и кроватей с фильтрацией по параметрам, чтобы покупатель быстро сужал выбор среди большого числа моделей.",
      ],
      columns: [
        { title: "Диваны", items: ["Коллекции", "Фильтрация по параметрам"] },
        { title: "Кровати", items: ["Коллекции", "Фильтрация по параметрам"] },
      ],
    },
    {
      id: "product",
      title: "Карточка мебели",
      paragraphs: [
        "Карточка товара показывает материалы, размеры и комплектацию — покупатель принимает решение без дополнительных вопросов менеджеру.",
      ],
      bullets: ["Характеристики и материалы", "Избранное и сравнение из карточки"],
    },
    {
      id: "projects",
      title: "Бренд и интерьер",
      paragraphs: [
        "Раздел готовых проектов показывает мебель VOSART в реальном интерьере — фирменные съёмки из проектного архива фабрики.",
      ],
      bullets: ["Раздел готовых проектов", "Фирменные интерьерные съёмки"],
    },
    {
      id: "b2b",
      title: "B2B-раздел",
      paragraphs: [
        "Отдельные страницы для партнёров и оптовых покупателей дополняют розничный каталог.",
      ],
      bullets: ["Раздел для партнёров", "Условия для оптовых клиентов"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [{ label: "Разработка интернет-магазина", to: routes.ecom }],
  },
};
